import { useContext, useEffect, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import { OperationHoursContext } from "features/bots/BotForm/OperationHours/OperationHours.context.ts";
import useMedia from "utils/hooks/useMedia.ts";
import useMeasure from "utils/hooks/useMeasure.ts";
import useConfirmModal from "common/Modal/useConfirmModal.tsx";
import {
  convertToLocalEvents,
  getParsedOperationHoursEvents,
} from "common/Calendars/utils.ts";
import { isEqual } from "lodash";
import { ToggleField } from "@shared/features/bots/BotForm/common/types.ts";
import { UNSAVED_CHANGES_WARNING_MESSAGE } from "@shared/common/constants.ts";
import moment from "moment-timezone";
import useBackButtonHandler from "utils/hooks/useBackButtonHandler";

interface OperationHoursDrawerProps {
  open?: boolean;
  closeDrawer: () => void;
}

const useOperationHoursDrawer = ({
  open,
  closeDrawer,
}: OperationHoursDrawerProps) => {
  const { operationHoursEvents, setOperationHoursEvents, toggleField } =
    useContext(BotFormContext)!;
  const { myEvents, setMyEvents } = useContext(OperationHoursContext)!;
  const noExtensionScreen = useMedia([`(max-width: 900px)`], [true], false);
  const [measureRef, { height: buttonsHeight }] = useMeasure<HTMLDivElement>();
  const { confirmModalContextHolder, openConfirmModal } = useConfirmModal({});
  const { registerBackButtonListener } = useBackButtonHandler();

  const defaultTimezone = moment.tz.guess();
  const [timezone, setTimezone] = useState<string>(defaultTimezone);
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    if (!noExtensionScreen) {
      setShowCalendar(false);
    }
  }, [noExtensionScreen]);

  const checkUnsavedChanges = () => {
    if (
      (!operationHoursEvents || !operationHoursEvents?.length) &&
      (!myEvents || !myEvents?.length)
    )
      return false;
    if (operationHoursEvents?.length !== myEvents?.length) return true;
    const parsedEvents = getParsedOperationHoursEvents(myEvents, timezone);
    return !isEqual(parsedEvents, operationHoursEvents);
  };

  const onApply = () => {
    const parsedEvents = getParsedOperationHoursEvents(
      myEvents || [],
      timezone
    );
    setOperationHoursEvents(parsedEvents);
    closeDrawer();
    toggleField(ToggleField.OPERATION_HOURS, !!parsedEvents?.length);
  };

  const onCloseDrawer = () => {
    closeDrawer();
    toggleField(ToggleField.OPERATION_HOURS, !!operationHoursEvents?.length);
  };

  useEffect(() => {
    if (operationHoursEvents?.length) {
      setMyEvents(convertToLocalEvents(operationHoursEvents || []));
      setTimezone(operationHoursEvents[0].timezone);
    } else {
      setMyEvents(undefined);
      setTimezone(defaultTimezone);
    }
  }, [operationHoursEvents, open]);

  useEffect(() => {
    if (open) {
      return registerBackButtonListener(() => {
        const hasUnsavedChanges = checkUnsavedChanges();
        if (hasUnsavedChanges) {
          openConfirmModal(UNSAVED_CHANGES_WARNING_MESSAGE, onCloseDrawer);
          return true;
        }
        return false;
      });
    }
  }, [
    open,
    myEvents,
    operationHoursEvents,
    checkUnsavedChanges,
    openConfirmModal,
    onCloseDrawer,
    registerBackButtonListener,
  ]);

  const handleCloseDrawer = () => {
    const hasUnsavedChanges = checkUnsavedChanges();
    if (hasUnsavedChanges) {
      openConfirmModal(UNSAVED_CHANGES_WARNING_MESSAGE, onCloseDrawer);
    } else {
      onCloseDrawer();
    }
  };

  const resetCalendar = () => {
    openConfirmModal("Are you sure you want to reset the calendar?", () =>
      setMyEvents([])
    );
  };

  return {
    handleCloseDrawer,
    resetCalendar,
    onApply,
    showCalendar,
    measureRef,
    buttonsHeight,
    confirmModalContextHolder,
    timezone,
    setTimezone,
    defaultTimezone,
    myEvents,
    setMyEvents,
    noExtensionScreen,
    setShowCalendar,
  };
};

export default useOperationHoursDrawer;
